import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Mail, Lock, Eye, EyeOff } from "lucide-react";
import { Logo } from "../components/Logo";
import { Spinner } from "../components/common";
import { useAuth } from "../context/AuthContext";
import { errMsg } from "../lib/api";
import { track } from "../lib/analytics";

export function GoogleButton() {
  const handle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/app";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button data-testid="google-auth-btn" onClick={handle} type="button"
      className="w-full h-14 rounded-2xl border border-border bg-card font-semibold flex items-center justify-center gap-3 transition-transform duration-200 active:scale-[0.98]">
      <img alt="" src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" className="w-5 h-5" />
      Continue with Google
    </button>
  );
}

export function Field({ icon: Icon, right, ...props }) {
  return (
    <div className="relative">
      <Icon size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <input {...props}
        className="w-full h-14 pl-12 pr-12 rounded-2xl bg-secondary border border-transparent focus:border-primary focus:bg-card outline-none transition-colors text-[15px]" />
      {right}
    </div>
  );
}

export default function Login() {
  const nav = useNavigate();
  const { login, verify2fa } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null); // { challenge_id, email_hint }
  const [code, setCode] = useState("");
  const [trustDevice, setTrustDevice] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    track("login_attempt", "/login");
    try {
      const res = await login(email, password, remember);
      if (res?.two_factor_required) {
        setTwofa({ challenge_id: res.challenge_id, email_hint: res.email_hint });
        toast.message(`We emailed a verification code to ${res.email_hint}`);
        return;
      }
      track("login_success", "/login");
      toast.success("Welcome back");
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const verifyCode = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await verify2fa(twofa.challenge_id, code, trustDevice);
      toast.success("Welcome back");
      nav("/app", { replace: true });
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  const resendCode = async () => {
    setBusy(true);
    try {
      const res = await login(email, password, remember);
      if (res?.two_factor_required) {
        setTwofa({ challenge_id: res.challenge_id, email_hint: res.email_hint });
        setCode("");
        toast.success("New code sent");
      }
    } catch (err) {
      toast.error(errMsg(err));
    } finally { setBusy(false); }
  };

  if (twofa) {
    return (
      <div className="min-h-screen w-full flex justify-center bg-background">
        <div className="w-full max-w-md bg-mesh min-h-screen px-6 pt-10 pb-10 flex flex-col">
          <Logo size={36} />
          <div className="mt-10 animate-fade-up">
            <h1 className="font-display text-4xl font-light tracking-tighter">Verify it's you</h1>
            <p className="text-muted-foreground mt-2">
              Enter the 6-digit code we emailed to <span className="font-semibold text-foreground">{twofa.email_hint}</span>.
            </p>
          </div>
          <form onSubmit={verifyCode} className="mt-8 space-y-4">
            <input data-testid="twofa-code-input" inputMode="numeric" maxLength={6} autoFocus
              placeholder="000000" value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              className="w-full h-16 px-4 rounded-2xl bg-secondary border border-transparent focus:border-primary focus:bg-card outline-none text-center text-3xl font-semibold tracking-[0.5em]" />
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="twofa-trust-label">
              <input type="checkbox" checked={trustDevice} onChange={(e) => setTrustDevice(e.target.checked)}
                data-testid="twofa-trust-checkbox"
                className="w-4 h-4 rounded border-border accent-primary cursor-pointer" />
              <span className="text-sm text-muted-foreground">Trust this device for 30 days</span>
            </label>
            <button type="submit" disabled={busy || code.length < 6} data-testid="twofa-verify-btn"
              className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 shadow-[0_12px_32px_rgba(75,35,204,0.3)] transition-transform duration-200 active:scale-[0.98] disabled:opacity-60">
              {busy ? <Spinner className="w-5 h-5" /> : "Verify & sign in"}
            </button>
          </form>
          <div className="flex items-center justify-between mt-4">
            <button type="button" onClick={resendCode} disabled={busy} data-testid="twofa-resend-btn"
              className="text-sm font-semibold text-primary disabled:opacity-60">Resend code</button>
            <button type="button" onClick={() => { setTwofa(null); setCode(""); }} data-testid="twofa-back-btn"
              className="text-sm font-semibold text-muted-foreground">Back to sign in</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex justify-center bg-background">
      <div className="w-full max-w-md bg-mesh min-h-screen px-6 pt-10 pb-10 flex flex-col">
        <Logo size={36} />
        <div className="mt-10 animate-fade-up">
          <h1 className="font-display text-4xl font-light tracking-tighter">Welcome back</h1>
          <p className="text-muted-foreground mt-2">Sign in to your Jack Cash wallet.</p>
        </div>

        <form onSubmit={submit} className="mt-8 space-y-3">
          <Field icon={Mail} type="email" required placeholder="Email address" data-testid="login-email-input"
            value={email} onChange={(e) => setEmail(e.target.value)} />
          <Field icon={Lock} type={show ? "text" : "password"} required placeholder="Password" data-testid="login-password-input"
            value={password} onChange={(e) => setPassword(e.target.value)}
            right={<button type="button" onClick={() => setShow(!show)} className="absolute right-4 top-1/2 -translate-y-1/2 text-muted-foreground">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>} />
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me-label">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)}
                data-testid="remember-me-checkbox"
                className="w-4 h-4 rounded border-border accent-primary cursor-pointer" />
              <span className="text-sm text-muted-foreground">Remember me</span>
            </label>
            <Link to="/forgot-password" data-testid="forgot-password-link" className="text-sm font-semibold text-primary">Forgot password?</Link>
          </div>
          <button type="submit" disabled={busy} data-testid="login-submit-btn"
            className="w-full h-14 rounded-2xl bg-primary text-primary-foreground font-semibold flex items-center justify-center gap-2 shadow-[0_12px_32px_rgba(75,35,204,0.3)] transition-transform duration-200 active:scale-[0.98] disabled:opacity-60">
            {busy ? <Spinner className="w-5 h-5" /> : "Sign in"}
          </button>
        </form>

        <div className="flex items-center gap-4 my-6">
          <div className="h-px bg-border flex-1" /><span className="text-xs text-muted-foreground">OR</span><div className="h-px bg-border flex-1" />
        </div>
        <GoogleButton />

        <p className="text-center text-sm text-muted-foreground mt-8">
          New to Jack Cash? <Link to="/signup" data-testid="go-signup-link" className="text-primary font-semibold">Create an account</Link>
        </p>
      </div>
    </div>
  );
}
